import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

/** Famille commerciale (Lot 9.0-E.B) -- distincte de la catégorie médicale, jamais exposée côté client. */
export interface MarketCatalogFamily {
  code: string;
  label: string;
  position: number;
}

/** Item display-only d'un bilan (ex. "HDL-cholestérol") -- jamais un objet biologique structuré. */
export interface MarketCatalogBilanItem {
  label: string;
  position: number;
}

/** Un bilan de la bibliothèque Waitora -- jamais needId/coverage médicale/priorityTier. */
export interface MarketCatalogBilan {
  code: string;
  name: string;
  description: string | null;
  familyCode: string;
  items: MarketCatalogBilanItem[];
  /**
   * Envoyé par le backend depuis V318 (`GET /api/v1/prevention/catalog/bilans`, toujours présent) --
   * code de la catégorie de prévention (Need -> Category) d'un bilan RECOMMENDATION_ENABLED, `null`
   * pour un bilan qui ne couvre aucun besoin recommandable (CATALOG_ONLY / SUPPLEMENTAL_ALLOWED).
   * Jamais confondu avec `familyCode` (taxonomie commerciale, indépendante des catégories).
   * `undefined` reste géré par `OfferFormComponent` (règle prudente) pour toute source de bilans qui
   * ne passerait pas par cet endpoint, mais ne devrait plus se produire en pratique.
   */
  recommendableCategoryCode?: string | null;
}

/** Un bilan référencé dans un ReferenceOffer (Lot 9.0-E.E.E) -- jamais needId/coverage médicale. */
export interface MarketCatalogReferenceOfferBilan {
  code: string;
  name: string;
  position: number;
}

/** Extra commercial display-only d'un ReferenceOffer (Lot 9.0-E.E.E) -- jamais un PreventionBilan. */
export interface MarketCatalogAdditionalItem {
  label: string;
  position: number;
}

/**
 * Template prêt à copier de la bibliothèque Waitora (Lot 9.0-E.E.E) -- jamais activable
 * directement, jamais de prix (aucun defaultPrice/marketPrice, section 26 du lot). Point de
 * départ pour créer une Offer via `/prevention-offres/nouvelle?fromTemplate=<code>` -- une copie,
 * jamais un lien vivant vers ce template après création.
 */
export interface MarketCatalogReferenceOffer {
  code: string;
  name: string;
  description: string | null;
  familyCode: string;
  bilans: MarketCatalogReferenceOfferBilan[];
  additionalItems: MarketCatalogAdditionalItem[];
  hasPhoto: boolean;
  photoUpdatedAt: string | null;
}

/** Catalogue commercial Waitora (Lot 9.0-E.B) -- lecture seule, IDENTIQUE pour toute organisation. */
@Injectable({
  providedIn: 'root'
})
export class PreventionCatalogService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiUrl}/api/v1/prevention/catalog`;

  getFamilies(): Observable<MarketCatalogFamily[]> {
    return this.http.get<MarketCatalogFamily[]>(`${this.baseUrl}/families`);
  }

  getBilans(): Observable<MarketCatalogBilan[]> {
    return this.http.get<MarketCatalogBilan[]>(`${this.baseUrl}/bilans`);
  }

  /** Bibliothèque Waitora (Lot 9.0-E.E.E) -- 18 templates, aucun filtre familyCode requis côté client. */
  getReferenceOffers(): Observable<MarketCatalogReferenceOffer[]> {
    return this.http.get<MarketCatalogReferenceOffer[]>(`${this.baseUrl}/reference-offers`);
  }

  /** URL publique de la photo par défaut d'un ReferenceOffer (pas d'auth requise -- un <img src> n'envoie
   *  jamais l'en-tête Authorization), avec cache-busting sur photoUpdatedAt -- même idiome que
   *  PreventionOffersService#getPhotoUrl côté Offer organisation-scopée. Résolue par code (jamais un id,
   *  jamais exposé par MarketCatalogReferenceOffer). */
  getReferenceOfferPhotoUrl(code: string, photoUpdatedAt?: string | null): string {
    const url = `${environment.apiUrl}/public/v1/prevention/catalog/reference-offers/${code}/photo`;
    return photoUpdatedAt ? `${url}?v=${encodeURIComponent(photoUpdatedAt)}` : url;
  }
}
